import { type Contact } from 'src/api/generated';
import { useAlert } from 'src/contexts/alert-context';
import { useUpdateContactMutation } from '../../../../hooks/api/contacts-mutations';
import { InlineTextInput } from 'src/modules/shared/components/molecules/inputs/inline/inline-text-input';
import { ContactCustomFieldsSection } from './contact-custom-fields-section';
import { ExpandableSection } from 'src/modules/shared/components/molecules/expandable-section';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Route as CommunicationsRoute } from 'src/routes/communications';
import type { CommunicationsSearch } from 'src/modules/shared/schemas/communications-search.schema';
import { Route as SettingsCustomFieldsRoute } from 'src/routes/settings/custom-fields';
import { settingsSearchSchema } from 'src/modules/settings/schemas/settings.schema';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { PiTableLight } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { CrmCustomFieldPermission } from 'src/modules/shared/types/permissions.type';
import { DetailsSectionReveal } from './details-section-reveal';
import { DetailsFieldListSkeleton } from './details-field-list-skeleton-parts';

interface ContactFieldsSectionProps {
  contact: Contact | null;
  loading?: boolean;
  error?: Error | null;
}

export const ContactFieldsSection: React.FC<ContactFieldsSectionProps> = ({
  contact,
  loading = false,
  error = null,
}) => {
  const { t } = useI18n();
  const titleClassName = 'text-xs sm:text-xs uppercase text-secondary-500';
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const { hasPermission: canCreateCustomFields, loading: loadingPermissions } =
    useHasPermissions(CrmCustomFieldPermission.Create);
  const { hasPermission: canUpdateCustomFields } = useHasPermissions(
    CrmCustomFieldPermission.Update
  );
  const canEditCustomFields = canCreateCustomFields || canUpdateCustomFields;
  const { funnelId } = useSearch({
    from: CommunicationsRoute.id,
    select: (search: CommunicationsSearch) => ({
      funnelId: search.funnelId,
    }),
  });

  const { updateContact, loading: saving } = useUpdateContactMutation(
    () => undefined,
    () => {
      showAlert(
        t('communications.contactPanel.contact.info.updateError'),
        'error'
      );
    }
  );

  const handleUpdateField = (field: keyof Contact) => async (value: string) => {
    if (!contact) return;

    await updateContact({
      id: contact.id,
      data: {
        [field]: value,
      },
    });
  };

  const handleCreateCustomFieldClick = () => {
    if (!funnelId) return;

    void navigate({
      to: SettingsCustomFieldsRoute.to,
      search: () =>
        settingsSearchSchema.parse({
          subtab: 'custom-fields',
          scope: 'funnel',
          funnelId,
          view: 'create',
          entity: 'CONTACT',
        }),
    });
  };

  const actions =
    !loadingPermissions && canEditCustomFields ? (
      <div className="hidden sm:flex">
        <Tooltip
          enabled={!!funnelId}
          content={t('communications.contactPanel.contact.customFields.create')}
          placement="bottom"
          size="sm"
        >
          <Button
            variant="neutral"
            appearance="ghost"
            size="sm"
            isIconOnly
            disabled={!funnelId}
            onClick={handleCreateCustomFieldClick}
          >
            <PiTableLight className="h-4 w-4" />
          </Button>
        </Tooltip>
      </div>
    ) : undefined;

  const content = (() => {
    if (loading) {
      return (
        <DetailsFieldListSkeleton
          fieldCount={2}
          variant="editable"
          revealCount={2}
        />
      );
    }

    if (!contact || error) {
      return (
        <ErrorState
          title={t('communications.contactPanel.contact.info.error.title')}
          description={t(
            'communications.contactPanel.contact.info.error.description'
          )}
        />
      );
    }

    const items: React.ReactNode[] = [
      <InlineTextInput
        key="firstName"
        label={t('communications.contactPanel.contact.info.firstName')}
        value={contact.firstName ?? ''}
        placeholder={t(
          'communications.contactPanel.contact.info.firstNamePlaceholder'
        )}
        onSave={handleUpdateField('firstName')}
        loading={saving}
        size="md"
        variant="neutral"
        appearance="soft"
        layout="stacked"
      />,
      <InlineTextInput
        key="lastName"
        label={t('communications.contactPanel.contact.info.lastName')}
        value={contact.lastName ?? ''}
        placeholder={t(
          'communications.contactPanel.contact.info.lastNamePlaceholder'
        )}
        onSave={handleUpdateField('lastName')}
        loading={saving}
        size="md"
        variant="neutral"
        appearance="soft"
        layout="stacked"
      />,
      <InlineTextInput
        key="nationalId"
        label={t('communications.contactPanel.contact.info.nationalId')}
        value={contact.nationalId ?? ''}
        placeholder={t(
          'communications.contactPanel.contact.info.nationalIdPlaceholder'
        )}
        onSave={handleUpdateField('nationalId')}
        loading={saving}
        size="md"
        variant="neutral"
        appearance="soft"
        layout="stacked"
      />,
      <InlineTextInput
        key="companyPosition"
        label={t('communications.contactPanel.contact.info.companyPosition')}
        value={contact.companyPosition ?? ''}
        placeholder={t(
          'communications.contactPanel.contact.info.companyPositionPlaceholder'
        )}
        onSave={handleUpdateField('companyPosition')}
        loading={saving}
        size="md"
        variant="neutral"
        appearance="soft"
        layout="stacked"
      />,
    ];

    return (
      <div className="flex flex-col gap-3">
        <DetailsSectionReveal items={items} initiallyVisible={2} />
        <ContactCustomFieldsSection contact={contact} />
      </div>
    );
  })();

  return (
    <ExpandableSection
      title={t('communications.contactPanel.contact.info.title')}
      titleClassName={titleClassName}
      className="bg-neutral-50/40"
      actions={actions}
      contentClassName="max-h-none min-h-[4rem]"
      compactHeaderOnMobile
    >
      {content}
    </ExpandableSection>
  );
};
